import { useEffect, useState, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/notifications?limit=20");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_e) {/* */}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const handleClick = async (n) => {
    if (!n.read_at) {
      await api.post(`/me/notifications/${n.id}/read`);
      load();
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    await api.post("/me/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          className="relative h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <Bell className="h-4 w-4 text-foreground" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <div className="font-semibold">Notifications</div>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              id="notif-mark-all-read"
              data-testid="notification-mark-all-read"
              onClick={markAll}
              className="text-xs rounded-full"
            >
              <Check className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No notifications yet.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={
                  "w-full text-left p-3 border-b border-border hover:bg-secondary transition-colors " +
                  (!n.read_at ? "bg-accent/30" : "")
                }
              >
                <div className="flex items-start gap-2">
                  {!n.read_at && <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
